import {PaymentStatusModel} from "../../../../shared/models/order/payment-status.model";
import BarekamChip from "../../../../components/chip/barekamChip";
import {GapContainer} from "../../../../components/card/styled";
import BarekamText from "../../../../components/text";
import {PaperProps, Tooltip} from "@mui/material";
import {InfoOutlined} from "@mui/icons-material";
import moment from "moment/moment";
import {PaymentInfoModel} from "../../../../shared/models/order/payment-info.model";
import {Recipient} from "../../../../shared/models/user/recipient/recipient";
import {TrackingRecord} from "../../../../shared/models/order/trackingRecord.model";
import {ShippingInfoModel} from "../../../../shared/models/order/shipping-info.model";
import {OrderModel} from "../../../../shared/models/order/order-item.model";
import React from "react";
import {OrdersTableColumn} from "../../models/column-models";
import {useAppSelector} from "../../../../../redux/store";
import {Message, MessageSections} from "../../../../components/message/message";
import {AdditionalServiceModel} from "../../../../shared/models/order/additional-service.model";
import {DurationChip} from "../../components/duration-chip/duration-chip";
import {getMessage} from "../../../../shared/helper-functions";

export const CardRowData = ({order, item}: CardRowDataProps) => {
    const {messageCache, languageId} = useAppSelector(state => state.messagesReducer);

    switch (item.variant) {
        case 'paymentStatus':
            const paymentStatus = order[item.field] as PaymentStatusModel;

            return <BarekamChip messageKey={paymentStatus.name}
                                statusId={paymentStatus.paymentStatusId}
                                chipSize={"s"}/>
        case 'date':
            const isOnHold = order.additionalService?.some(service => service.additionalServiceId === 6);

            if (isOnHold) {
                return (
                    <GapContainer>
                        <BarekamText variant={"m"} opaque>
                            <Message messageKey={'order_pending'} sectionId={MessageSections.ORDERS}/>
                        </BarekamText>

                        <Tooltip title={getMessage('order_pending_hint', MessageSections.ORDERS, messageCache, languageId)}>
                            <InfoOutlined opacity={0.6}/>
                        </Tooltip>
                    </GapContainer>
                )
            } else {
                return (
                    <BarekamText variant={"m"}>
                        {moment(order[item.field] as number).format('DD/MM/YYYY')}
                    </BarekamText>
                )
            }
        case 'money':
            const price = (order[item.field] as PaymentInfoModel)?.shippingAmount;
            const currency = (order[item.field] as PaymentInfoModel)?.shippingAmountCurrency;

            return <BarekamText variant={"m"}>
                {price + ' ' + currency?.name[languageId]}
            </BarekamText>
        case 'recipient':
            return order[item.field]
                ? <BarekamText variant={"m"}>
                    {(order[item.field] as Recipient).firstName} {(order[item.field] as Recipient).lastName}</BarekamText>
                : <></>
        case 'warehouse':
            return <BarekamText variant={"m"}>
                {order.shippingInfo?.destinationWarehouse?.city}
            </BarekamText>
        case 'weight':
            return <BarekamText variant={"m"}>{order.weight}
            </BarekamText>
        case 'price':
            return <BarekamText variant={"m"}>
                {order.paymentInfo?.shippingAmount} {order.paymentInfo?.shippingAmountCurrency?.sign}
            </BarekamText>
        case 'productCount':
            const collectedProductsAmount = order.products.filter(product => product.isInWarehouse).length;
            const allProductsAmount = order.products.length;

            return <BarekamText variant={"m"}>
                {collectedProductsAmount}/{allProductsAmount}
            </BarekamText>
        case 'productTrackNumbers':
            return (
                <BarekamText variant={"m"} overflow={'hidden'}>
                    {
                        order.products
                            .map(product => product.trackingNumber)
                            .join(', ')
                    }
                </BarekamText>
            )
        case 'trackRecord':
            return <DurationChip trackingRecord={order[item.field] as TrackingRecord[]}/>
        case 'sender':
            if ((order[item.field] as ShippingInfoModel).sendBy) {
                return (
                    <BarekamText variant={"m"}>
                        <Message messageKey={'send_by_friend'} sectionId={MessageSections.ORDERS}/>
                    </BarekamText>
                )
            } else {
                return (
                    <BarekamText variant={"m"} overflow={'hidden'}>
                        {
                            order.products
                                .map(product => product.store?.name && product.store?.name[languageId])
                                .filter(val => !!val)
                                .join(', ')
                        }
                    </BarekamText>
                )
            }
        case 'shippingCategory':
            if ((order[item.field] as ShippingInfoModel).sendBy) {
                return (
                    <BarekamChip messageKey={'barekam_shipping_category'}
                                 statusId={1}
                                 chipSize={'s'}/>
                )
            }
            return (
                <BarekamChip messageKey={'online_shipping_category'}
                             statusId={3}
                             chipSize={'s'}/>
            )
        case 'additionalServices':
            return (
                <div style={{display: 'flex', flexDirection: 'column', gap: '0.25rem'}}>
                    {(order[item.field] as AdditionalServiceModel[]).map(service =>
                        <BarekamChip messageKey={service.name}
                                     statusId={5}
                                     chipSize={'s'}/>
                    )}
                </div>
            )
        default:
            return <BarekamText variant={"s"} bold>
                {String(order[item.field as keyof OrderModel])}
            </BarekamText>
    }
}

interface CardRowDataProps extends PaperProps {
    order: OrderModel;
    item: OrdersTableColumn;
}
